<template>
  <ContentSection spacing="none" class="pb-16 transition-[height] sm:pb-28" :class="{ 'pt-12 sm:pt-18': index > 0 }">
    <DownloadCenterFilter
      v-if="!data.selected_category"
      v-model="filter"
      :categories="categoriesList"
      :languages="languagesList"
      class="mb-20"
    />
    <!-- why no margin? in filter? -->
    <Container use-full-width>
      <TransitionGroup
        tag="div"
        name="list"
        class="space-y-32"
      >
        <div v-for="category in filteredCategories" :key="category.id">
          <Downloads :category="category" />
        </div>
      </TransitionGroup>
      <Transition name="fade-in">
        <NoEntriesFound v-if="!filteredCategories?.length" class="mb-20 mt-40" />
      </Transition>
    </Container>
  </ContentSection>
</template>

<script setup lang="ts">
import type { MainComponentsDownloadcenterFragment } from '#gql'

const runtimeConfig = useRuntimeConfig()
const apiUrl = runtimeConfig.public.API_URL
const route = useRoute()
const { t, localeProperties } = useI18n()
const { perfionCulture } = localeProperties.value

const props = defineProps<{
  data: Enforce<MainComponentsDownloadcenterFragment>
  index: number
}>()

const perfionData = await useFetch(apiUrl + '/perfion/downloads')

const categories = (perfionData.data.value as { data: DownloadCategory[] } | undefined)?.data ?? []

export type DownloadFilterType = {
  category: string | null
  searchTerm: string
  language: string | null
}

const filter = ref<DownloadFilterType>({
  category: route.query.category ? (route.query.category as string) : 'all',
  searchTerm: route.query.search ? (route.query.search as string) : '',
  language: route.query.lang ? (route.query.lang as string) : 'all',
})

const categoriesList = computed(() => { // list of categories to populate filter
  return [
    {
      id: 'all',
      slug: 'all',
      title: t('all'),
    },
    ...categories.map(category => ({
      id: category.id,
      slug: category.id,
      title: t(`downloadcenter.${category.title}`),
    })),
  ]
})

const languagesList = computed(() => { // list of languages to populate filter
  const languages = [
    ...new Set(categories.flatMap(category =>
      Object.values(category.products).flatMap(product =>
        Object.values(product.files).flatMap(file =>
          file.languages ? file.languages.filter(lang => lang !== null && lang !== undefined && lang !== '') : [])))),
  ]

  return [
    {
      id: 'all',
      slug: 'all',
      title: t('all'),
    },
    ...languages.map(language => ({
      id: language,
      slug: language,
      title: language,
    })),
  ]
})

const filteredCategories = computed(() => {
  let filterResult = categories

  // CATEGORY
  if (filter.value.category !== 'all' || props.data.selected_category) {
    const filterCategoryId = props.data.selected_category?.value ?? filter.value.category

    filterResult = filterResult.filter(category => category.id === filterCategoryId)
  }

  // SEARCH
  if (filter.value.searchTerm !== '') {
    filterResult = filterResult
      .map(category => ({
        ...category,
        products: category.products.filter(product =>
          product.title[perfionCulture]?.toLowerCase().includes(filter.value.searchTerm.toLowerCase())),
      }))
      .filter(category => category.products.length > 0) // Remove categories without matching products
  }

  // LANGUAGE
  if (filter.value.language !== 'all') {
    filterResult = filterResult
      .map(category => ({
        ...category,
        products: Object.values(category.products)
          .map(product => ({
            ...product,
            files: Object.values(product.files).filter(file =>
              Array.isArray(file.languages) && filter.value.language ? file.languages.includes(filter.value.language) : false),
          }))
          .filter(product => product.files.length > 0), // Remove products without matching files
      }))
      .filter(category => category.products.length > 0) // Remove categories without matching products
  }

  return filterResult
})

watch(
  () => filter.value,
  () => {
    saveFilterInUrl()
  },
  { deep: true },
)

// Saves the filter in the URL
const router = useRouter()
function saveFilterInUrl() {
  router.push({
    query: {
      category: filter.value.category,
      search: filter.value.searchTerm,
      lang: filter.value.language,
    },
  })
}
</script>
